// `<ToastContainer />` — single mount point at the app shell rendering
// the toast queue. Mounted in `src/app/app.tsx`, fixed top-right per
// Banks Ask 1 (build-plan default). Per-toast item handles its own
// auto-dismiss timer; error toasts persist until manual dismiss.
//
// **Why per-item timer (not a queue-wide setInterval).** Each toast's
// dismissal is independent + idempotent; the per-item `useEffect`
// schedules `setTimeout(..., AUTO_DISMISS_MS)` on mount and clears on
// unmount. Error toasts skip the schedule entirely; the user dismisses
// them via the visible × button. Pause-on-hover is a P-036 candidate
// (a11y polish); V1 ships the simpler timer.
//
// **Manual dismiss button.** Every toast carries a × button labeled
// "Dismiss notification" (sr-only) plus the visible glyph. Error
// toasts keep this button as their only dismissal path.
//
// **Position: fixed top-right (Banks Ask 1 → option a).** Matches the
// build-plan default. `z-50` puts it above modals' `z-40` overlay.
// `pointer-events-none` on the container lets clicks fall through to
// underlying UI; `pointer-events-auto` on each toast item keeps them
// interactive.

import { useEffect } from 'preact/hooks'

import type { Toast, ToastKind } from './toast-store'
import { AUTO_DISMISS_MS, removeToast } from './toast-store'
import { useToast } from './use-toast'

const KIND_CLASS: Record<ToastKind, string> = {
  success: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  error: 'border-red-300 bg-red-50 text-red-900',
  info: 'border-blue-300 bg-blue-50 text-blue-900',
  warning: 'border-amber-300 bg-amber-50 text-amber-900',
}

const KIND_ROLE: Record<ToastKind, 'status' | 'alert'> = {
  success: 'status',
  info: 'status',
  warning: 'alert',
  error: 'alert',
}

export function ToastContainer() {
  const { toasts } = useToast()
  if (toasts.length === 0) return null
  return (
    <div
      class="pointer-events-none fixed right-4 top-4 z-50 flex w-full max-w-sm flex-col gap-2"
      aria-label="Notifications"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  )
}

function ToastItem({ toast }: { toast: Toast }) {
  const persistent = toast.kind === 'error'

  useEffect(() => {
    if (persistent) return undefined
    const timer = setTimeout(() => removeToast(toast.id), AUTO_DISMISS_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [toast.id, persistent])

  const handleAction = () => {
    toast.action?.onClick()
    removeToast(toast.id)
  }

  return (
    <div
      role={KIND_ROLE[toast.kind]}
      aria-live={toast.kind === 'error' ? 'assertive' : 'polite'}
      class={`pointer-events-auto flex items-start gap-2 rounded border px-3 py-2 text-sm shadow ${KIND_CLASS[toast.kind]}`}
    >
      <span class="flex-1 break-words">{toast.message}</span>
      {toast.action !== undefined && (
        <button
          type="button"
          onClick={handleAction}
          class="shrink-0 rounded px-1.5 py-0.5 text-xs font-medium underline underline-offset-2 hover:bg-current/10 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={() => removeToast(toast.id)}
        class="shrink-0 rounded text-base leading-none text-current/70 hover:text-current focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Dismiss notification"
      >
        ×
      </button>
    </div>
  )
}
